import React, { useState, FormEvent } from 'react';
import { api } from '../services/api';
import { useStore } from '../hooks/useStore';
import type {
  LoginResponse,
  MFAMethod,
  MFARequiredResponse,
  MFASetupRequiredResponse,
  TOTPSetupResponse,
} from '../types';

type AuthErrorPayload = MFASetupRequiredResponse | MFARequiredResponse | string | { error?: string; message?: string };
type LoginStage = 'password' | 'setup-select' | 'setup-totp' | 'challenge-select' | 'challenge-totp';

const methodLabel: Record<MFAMethod, string> = {
  totp: 'Authenticator App',
  webauthn: 'Security Key or Device Passkey',
};

const isObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null
);

const base64urlToBuffer = (input: string): ArrayBuffer => {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(input.length / 4) * 4, '=');
  const binary = window.atob(padded);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return buffer;
};

const bufferToBase64url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const bufferSourceToBase64url = (value: ArrayBuffer | ArrayBufferView): string => {
  if (value instanceof ArrayBuffer) {
    return bufferToBase64url(value);
  }
  const view = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  const copy = new Uint8Array(view.byteLength);
  copy.set(view);
  return bufferToBase64url(copy.buffer);
};

const isBufferSource = (value: unknown): value is ArrayBuffer | ArrayBufferView => (
  value instanceof ArrayBuffer || ArrayBuffer.isView(value)
);

const decodeCredentialList = (value: unknown): PublicKeyCredentialDescriptor[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value
    .filter(isObject)
    .map((item) => ({
      ...item,
      id: base64urlToBuffer(String(item.id)),
    })) as PublicKeyCredentialDescriptor[];
};

const publicKeyFromOptions = (raw: unknown): Record<string, unknown> => {
  if (!isObject(raw)) {
    throw new Error('Passkey options were not valid');
  }
  const nested = raw.publicKey;
  if (isObject(nested)) {
    return nested;
  }
  return raw;
};

const decodeCreationOptions = (raw: unknown): PublicKeyCredentialCreationOptions => {
  const publicKey = publicKeyFromOptions(raw);
  const user = isObject(publicKey.user) ? publicKey.user : {};
  return {
    ...publicKey,
    challenge: base64urlToBuffer(String(publicKey.challenge)),
    user: {
      ...user,
      id: base64urlToBuffer(String(user.id)),
      name: String(user.name ?? ''),
      displayName: String(user.displayName ?? user.name ?? ''),
    },
    excludeCredentials: decodeCredentialList(publicKey.excludeCredentials),
  } as PublicKeyCredentialCreationOptions;
};

const decodeAssertionOptions = (raw: unknown): PublicKeyCredentialRequestOptions => {
  const publicKey = publicKeyFromOptions(raw);
  return {
    ...publicKey,
    challenge: base64urlToBuffer(String(publicKey.challenge)),
    allowCredentials: decodeCredentialList(publicKey.allowCredentials),
  } as PublicKeyCredentialRequestOptions;
};

const credentialToJSON = (credential: Credential): Record<string, unknown> => {
  const publicKeyCredential = credential as PublicKeyCredential;
  const response = publicKeyCredential.response;
  const responseRecord = response as unknown as Record<string, unknown>;
  const responseJSON: Record<string, unknown> = {
    clientDataJSON: bufferSourceToBase64url(response.clientDataJSON),
  };

  if (isBufferSource(responseRecord.attestationObject)) {
    responseJSON.attestationObject = bufferSourceToBase64url(responseRecord.attestationObject);
  }
  if (isBufferSource(responseRecord.authenticatorData)) {
    responseJSON.authenticatorData = bufferSourceToBase64url(responseRecord.authenticatorData);
  }
  if (isBufferSource(responseRecord.signature)) {
    responseJSON.signature = bufferSourceToBase64url(responseRecord.signature);
  }
  if (isBufferSource(responseRecord.userHandle)) {
    responseJSON.userHandle = bufferSourceToBase64url(responseRecord.userHandle);
  }

  return {
    id: publicKeyCredential.id,
    rawId: bufferSourceToBase64url(publicKeyCredential.rawId),
    type: publicKeyCredential.type,
    response: responseJSON,
    clientExtensionResults: publicKeyCredential.getClientExtensionResults(),
  };
};

const isSetupRequired = (payload: unknown): payload is MFASetupRequiredResponse => (
  isObject(payload) && payload.mfa_setup_required === true && typeof payload.setup_token === 'string'
);

const isMFARequired = (payload: unknown): payload is MFARequiredResponse => (
  isObject(payload) && payload.mfa_required === true && typeof payload.challenge_token === 'string'
);

const methodsFromPayload = (methods: unknown): MFAMethod[] => {
  if (!Array.isArray(methods)) {
    return ['totp'];
  }
  return methods.filter((method): method is MFAMethod => method === 'totp' || method === 'webauthn');
};

const errorText = (err: unknown): string => {
  const payload = (err as { response?: { data?: AuthErrorPayload } })?.response?.data;
  if (typeof payload === 'string') {
    return payload;
  }
  if (isObject(payload)) {
    return String(payload.error ?? payload.message ?? 'Authentication failed');
  }
  return err instanceof Error ? err.message : 'Authentication failed';
};

export const Login: React.FC = () => {
  const { setUser, setToken } = useStore();
  const [consented, setConsented] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [challengeCode, setChallengeCode] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [availableMethods, setAvailableMethods] = useState<MFAMethod[]>([]);
  const [stage, setStage] = useState<LoginStage>('password');
  const [totpSetup, setTotpSetup] = useState<TOTPSetupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const acceptLogin = (response: LoginResponse) => {
    setToken(response.token);
    setUser(response.user);
  };

  const resetMFAState = () => {
    setSetupCode('');
    setChallengeCode('');
    setSetupToken('');
    setChallengeToken('');
    setAvailableMethods([]);
    setTotpSetup(null);
    setStage('password');
  };

  const beginTOTPSetup = async () => {
    if (!setupToken) return;
    setError(null);
    setIsLoading(true);
    try {
      const setup = await api.prepareTOTP(setupToken);
      setTotpSetup(setup);
      setSetupCode('');
      setStage('setup-totp');
    } catch (err: unknown) {
      setError(errorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const finishTOTPSetup = async () => {
    if (!setupToken || !setupCode) return;
    setError(null);
    setIsLoading(true);
    try {
      acceptLogin(await api.finishTOTP(setupToken, setupCode));
    } catch (err: unknown) {
      setError(errorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const finishWebAuthnSetup = async () => {
    if (!setupToken) return;
    if (!window.PublicKeyCredential) {
      setError('This browser does not support security keys or passkeys.');
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const begin = await api.beginWebAuthnRegistration(setupToken);
      const credential = await navigator.credentials.create({
        publicKey: decodeCreationOptions(begin.options),
      });
      if (!credential) {
        throw new Error('Passkey setup was canceled');
      }
      acceptLogin(await api.finishWebAuthnRegistration(setupToken, credentialToJSON(credential)));
    } catch (err: unknown) {
      setError(errorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const verifyTOTP = async () => {
    if (!challengeToken || !challengeCode) return;
    setError(null);
    setIsLoading(true);
    try {
      acceptLogin(await api.verifyTOTP(challengeToken, challengeCode));
    } catch (err: unknown) {
      setError(errorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const verifyWebAuthn = async () => {
    if (!challengeToken) return;
    if (!window.PublicKeyCredential) {
      setError('This browser does not support security keys or passkeys.');
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const begin = await api.beginWebAuthnLogin(challengeToken);
      const credential = await navigator.credentials.get({
        publicKey: decodeAssertionOptions(begin.options),
      });
      if (!credential) {
        throw new Error('Passkey sign-in was canceled');
      }
      acceptLogin(await api.finishWebAuthnLogin(challengeToken, credentialToJSON(credential)));
    } catch (err: unknown) {
      setError(errorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    resetMFAState();

    try {
      acceptLogin(await api.login({ username, password }));
    } catch (err: unknown) {
      const payload = (err as { response?: { data?: AuthErrorPayload } })?.response?.data;
      if (isSetupRequired(payload)) {
        setSetupToken(payload.setup_token);
        setAvailableMethods(methodsFromPayload(payload.methods));
        setStage('setup-select');
        setError(null);
      } else if (isMFARequired(payload)) {
        setChallengeToken(payload.challenge_token);
        const methods = methodsFromPayload(payload.methods);
        setAvailableMethods(methods);
        setStage(methods.length === 1 && methods[0] === 'totp' ? 'challenge-totp' : 'challenge-select');
        setError(null);
      } else {
        setError(errorText(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderMethodButtons = (mode: 'setup' | 'login') => (
    <div className="mfa-method-list">
      {availableMethods.includes('webauthn') && (
        <button
          type="button"
          className="mfa-method-button"
          disabled={isLoading}
          onClick={mode === 'setup' ? finishWebAuthnSetup : verifyWebAuthn}
        >
          <span className="mfa-method-title">{methodLabel.webauthn}</span>
          <span className="mfa-method-detail">Use a device passkey, Windows Hello, phone, or hardware security key.</span>
        </button>
      )}
      {availableMethods.includes('totp') && (
        <button
          type="button"
          className="mfa-method-button"
          disabled={isLoading}
          onClick={mode === 'setup' ? beginTOTPSetup : () => setStage('challenge-totp')}
        >
          <span className="mfa-method-title">{methodLabel.totp}</span>
          <span className="mfa-method-detail">Use Google Authenticator, Microsoft Authenticator, 1Password, or another TOTP app.</span>
        </button>
      )}
    </div>
  );

  // Pre-login: System Use Notification (NIST 800-171 3.1.9 / AC-8)
  if (!consented) {
    return (
      <div className="login-container">
        <div className="cui-banner-bar">CUI</div>
        <div className="consent-card">
          <h2 className="consent-title">SYSTEM USE NOTIFICATION</h2>
          <div className="consent-body">
            <p>
              You are accessing an information system operated by Solutio Environmental, Inc.
              that processes, stores, and transmits Controlled Unclassified Information (CUI)
              in support of the U.S. Department of Defense.
            </p>
            <p>By using this system, you consent to the following conditions:</p>
            <ol>
              <li>
                This system is for authorized use only. Unauthorized use is prohibited
                and subject to criminal and civil penalties under 18 U.S.C. &sect; 1030
                and other applicable statutes.
              </li>
              <li>
                System usage may be monitored, recorded, and subject to audit at any time.
              </li>
              <li>
                Use of this system constitutes your consent to such monitoring, recording,
                and auditing.
              </li>
              <li>
                This system contains Controlled Unclassified Information (CUI) with specific
                safeguarding and handling requirements as prescribed by the Department of
                Defense and 32 CFR Part 2002.
              </li>
              <li>
                There is no expectation of privacy when using this system.
              </li>
              <li>
                Users must comply with all applicable CUI handling policies, procedures,
                and the organization&apos;s acceptable use policy.
              </li>
            </ol>
          </div>
          <button className="consent-button" onClick={() => setConsented(true)}>
            I Agree
          </button>
        </div>
        <div className="cui-footer-bar">CUI</div>
      </div>
    );
  }

  if (stage === 'setup-select') {
    return (
      <div className="login-container">
        <div className="cui-banner-bar">CUI</div>
        <div className="login-card mfa-card">
          <h1>Set Up Second Factor</h1>
          <p className="login-subtitle">Choose how you want to confirm VDI sign-ins.</p>
          {renderMethodButtons('setup')}
          {error && <div className="error-message"><span className="error-icon">!</span>{error}</div>}
          <button type="button" className="secondary-button" onClick={resetMFAState} disabled={isLoading}>
            Back
          </button>
        </div>
        <div className="cui-footer-bar">CUI</div>
      </div>
    );
  }

  if (stage === 'setup-totp' && totpSetup) {
    return (
      <div className="login-container">
        <div className="cui-banner-bar">CUI</div>
        <div className="login-card">
          <h1>Set Up Authenticator</h1>
          <p className="login-subtitle">Scan the QR code, then enter the current code.</p>

          <div className="totp-setup-box">
            {totpSetup.qr_data_uri && <img src={totpSetup.qr_data_uri} alt="Authenticator QR code" />}
            <div className="totp-secret">{totpSetup.secret_display}</div>
            <a href={totpSetup.otpauth_uri}>Open authenticator app</a>
          </div>

          <div className="form-group">
            <label htmlFor="setup-code">Authenticator code</label>
            <input
              id="setup-code"
              name="cui-vdi-setup-one-time-code"
              type="text"
              inputMode="numeric"
              value={setupCode}
              onChange={(e) => setSetupCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="Enter 6-digit one-time code"
              disabled={isLoading}
              autoComplete="one-time-code"
              aria-label="One-time authenticator code"
              title="One-time authenticator code"
              autoFocus
              required
            />
          </div>

          {error && <div className="error-message"><span className="error-icon">!</span>{error}</div>}

          <button
            type="button"
            disabled={isLoading || setupCode.length !== 6}
            className="login-button"
            onClick={finishTOTPSetup}
          >
            {isLoading ? 'Verifying...' : 'Finish Setup'}
          </button>
        </div>
        <div className="cui-footer-bar">CUI</div>
      </div>
    );
  }

  if (stage === 'challenge-select') {
    return (
      <div className="login-container">
        <div className="cui-banner-bar">CUI</div>
        <div className="login-card mfa-card">
          <h1>Confirm Sign-In</h1>
          <p className="login-subtitle">Use one of your enrolled second factors.</p>
          {renderMethodButtons('login')}
          {error && <div className="error-message"><span className="error-icon">!</span>{error}</div>}
          <button type="button" className="secondary-button" onClick={resetMFAState} disabled={isLoading}>
            Back
          </button>
        </div>
        <div className="cui-footer-bar">CUI</div>
      </div>
    );
  }

  if (stage === 'challenge-totp') {
    return (
      <div className="login-container">
        <div className="cui-banner-bar">CUI</div>
        <div className="login-card">
          <h1>Authenticator Code</h1>
          <p className="login-subtitle">Enter the current code from your authenticator app.</p>
          <div className="form-group">
            <label htmlFor="challenge-code">Authenticator code</label>
            <input
              id="challenge-code"
              name="cui-vdi-one-time-code"
              type="text"
              inputMode="numeric"
              value={challengeCode}
              onChange={(e) => setChallengeCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="Enter 6-digit one-time code"
              disabled={isLoading}
              autoFocus
              autoComplete="one-time-code"
              aria-label="One-time authenticator code"
              title="One-time authenticator code"
            />
          </div>
          {error && <div className="error-message"><span className="error-icon">!</span>{error}</div>}
          <button
            type="button"
            disabled={isLoading || challengeCode.length !== 6}
            className="login-button"
            onClick={verifyTOTP}
          >
            {isLoading ? 'Verifying...' : 'Continue'}
          </button>
          {availableMethods.includes('webauthn') && (
            <button type="button" className="secondary-button" onClick={() => setStage('challenge-select')} disabled={isLoading}>
              Use another method
            </button>
          )}
        </div>
        <div className="cui-footer-bar">CUI</div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="cui-banner-bar">CUI</div>
      <div className="login-card">
        <h1>VDI Login</h1>
        <p className="login-subtitle">Secure Virtual Desktop Access</p>

        <form onSubmit={handleSubmit} autoComplete="on">
          <div className="form-group">
            <label htmlFor="cui-cdi-username">Username</label>
            <input
              id="cui-cdi-username"
              name="cui-cdi-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              disabled={isLoading}
              autoComplete="section-cui-vdi username"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="cui-vdi-password">CUI Access Password</label>
            <input
              id="cui-vdi-password"
              name="cui-vdi-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your CUI access password"
              disabled={isLoading}
              autoComplete="section-cui-vdi current-password"
              required
            />
          </div>

          {error && <div className="error-message"><span className="error-icon">!</span>{error}</div>}

          <button
            type="submit"
            disabled={isLoading || !username || !password}
            className="login-button"
          >
            {isLoading ? 'Authenticating...' : 'Login'}
          </button>
        </form>

        <div className="login-footer">
          <p>Authorized Access Only</p>
        </div>
      </div>
      <div className="cui-footer-bar">CUI</div>
    </div>
  );
};
